// src/utils/surveyQuestions.js
// Unified team assessment questions mapped to Meeting Inventory
// Each question is 1-5 scale and can be compared directly to consultant observations
//
// July 2026 revision: 6 questions per category (was 10). Kept questions retain
// their original IDs so historical trends continue; retired IDs (sp3–sp9, cc2,
// cc3, cc5–cc10, hb1–hb3, hb8, hb9, rg1–rg10) never appear on new assessments
// but their answers remain in stored responses. Full old→new mapping in
// docs/question-reduction-worksheet.md.

// Teams store a snapshot of DEFAULT_CATEGORIES in teams/{id}.customQuestions.
// Run loaded snapshots through this before display: it upgrades snapshots that
// predate the July 2026 question revision (detected via the sp11_q sentinel)
// to the current question set, and applies category label renames
// (e.g. "The Right Genius" → "Right Chemistry").
export function withCurrentLabels(categories) {
  const cats = categories || [];
  const isCurrent = cats.some(cat => (cat.questions || []).some(q => q.id === 'sp11_q'));
  if (!isCurrent) return DEFAULT_CATEGORIES;
  return cats.map(cat =>
    cat.id === 'rightGenius' && cat.label !== 'Right Chemistry'
      ? { ...cat, label: 'Right Chemistry' }
      : cat
  );
}

export const DEFAULT_CATEGORIES = [
  {
    id: 'compellingContent',
    label: 'Compelling Content',
    color: '#5f7942',
    description: 'Does the team address important topics and drive meaningful outcomes?',
    inventoryGroup: 'compellingContent',
    questions: [
      {
        id: 'cc1_q',
        text: "Our discussions draw on the organization's clarity — purpose, values, and strategy.",
        inventoryItem: 'cc1'
      },
      {
        id: 'cc11_q',
        text: "The issues we discuss genuinely need the group's help and input.",
        inventoryItem: 'cc11'
      },
      {
        id: 'cc4_q',
        text: 'Our topics spark healthy debate.',
        inventoryItem: 'cc4'
      },
      {
        id: 'cc12_q',
        text: 'Our discussions lead to clear decisions.',
        inventoryItem: 'cc12'
      },
      {
        id: 'cc13_q',
        text: 'Our topics draw high levels of participation from the group.',
        inventoryItem: 'cc13'
      },
      {
        id: 'cc14_q',
        text: 'Topics are raised in the right setting — this meeting versus another time or forum.',
        inventoryItem: 'cc14'
      },
    ],
  },
  {
    id: 'healthyBehaviors',
    label: 'Healthy Behaviors',
    color: '#cba43a',
    description: 'Does the team demonstrate healthy communication and interpersonal dynamics?',
    inventoryGroup: 'healthyBehaviors',
    questions: [
      {
        id: 'hb11_q',
        text: 'Our leader encourages healthy conflict around ideas.',
        inventoryItem: 'hb11'
      },
      {
        id: 'hb4_q',
        text: 'Airtime is shared evenly — no one dominates, no one disappears.',
        inventoryItem: 'hb4'
      },
      {
        id: 'hb5_q',
        text: 'People stay engaged from the start of the meeting to the end.',
        inventoryItem: 'hb5'
      },
      {
        id: 'hb6_q',
        text: 'We ask questions to understand (inquiry) more than we push our own positions (advocacy).',
        inventoryItem: 'hb6'
      },
      {
        id: 'hb7_q',
        text: 'Comments are concise and move the dialogue forward.',
        inventoryItem: 'hb7'
      },
      {
        id: 'hb10_q',
        text: 'We use levity to reduce tension and boost collaboration.',
        inventoryItem: 'hb10'
      },
    ],
  },
  {
    id: 'rightGenius',
    label: 'Right Chemistry',
    color: '#7209b7',
    description: 'Does the team leverage the right skills and talents for different types of work?',
    inventoryGroup: 'rightGenius',
    questions: [
      {
        id: 'rg11_q',
        text: 'Topics are discussed with the right context — background, data, and framing — for the group to engage.',
        inventoryItem: 'rg11'
      },
      {
        id: 'rg12_q',
        text: 'Discussions stay at a consistent elevation — big picture or tactical — instead of bouncing between levels.',
        inventoryItem: 'rg12'
      },
      {
        id: 'rg13_q',
        text: "People's inputs build on the current discussion rather than derailing it.",
        inventoryItem: 'rg13'
      },
      {
        id: 'rg14_q',
        text: 'Frustrations get voiced and worked through rather than left to simmer.',
        inventoryItem: 'rg14'
      },
      {
        id: 'rg15_q',
        text: "Team members challenge and provoke each other's thinking.",
        inventoryItem: 'rg15'
      },
      {
        id: 'rg16_q',
        text: 'Our meetings have key moments — clear turning points where the conversation shifts or a breakthrough happens.',
        inventoryItem: 'rg16'
      },
    ],
  },
  {
    id: 'simpleProcess',
    label: 'Simple Process',
    color: '#2a3d5b',
    description: 'Does the team follow clear processes and manage meetings well?',
    inventoryGroup: 'simpleProcess', // Maps to Meeting Inventory group
    questions: [
      {
        id: 'sp1_q',
        text: 'The parts of our meetings are clear and useful.',
        inventoryItem: 'sp1' // Maps to inventory item for comparison
      },
      {
        id: 'sp2_q',
        text: 'Meeting roles (leader, facilitator, etc.) are clear, and people perform them well.',
        inventoryItem: 'sp2'
      },
      {
        id: 'sp11_q',
        text: 'Our meeting rules are clear, and we follow them.',
        inventoryItem: 'sp11'
      },
      {
        id: 'sp12_q',
        text: 'Decisions and action steps are clearly identified before the meeting ends.',
        inventoryItem: 'sp12'
      },
      {
        id: 'sp10_q',
        text: 'We determine the key messages to share with people outside the meeting.',
        inventoryItem: 'sp10'
      },
      {
        id: 'sp13_q',
        text: 'We make good use of tools in our meetings (handouts, visuals, technology, etc.).',
        inventoryItem: 'sp13'
      },
    ],
  },
];

export const SCALE_LABELS = {
  1: 'Almost Never',
  2: 'Rarely',
  3: 'Sometimes',
  4: 'Usually',
  5: 'Almost Always',
};

// Retired July 2026 — the Team Dynamics tab was removed from the assessment
// (roles absorbed the concept). Kept only so Results.js can render the
// personality types stored in historical responses' teamDynamics data.
export const PERSONALITY_TYPES = [
  { name: 'Bulldozer',        description: 'Dominates discussions.' },
  { name: 'Sergeant-at-arms', description: 'Ensures clarity among team members.' },
  { name: 'Spectator',        description: 'Disengages during meetings.' },
  { name: 'Class Clown',      description: 'Rarely gets serious about topics.' },
  { name: 'Columbo',          description: 'Asks numerous questions out of genuine curiosity.' },
  { name: 'Harmonizer',       description: 'Avoids conflict, potentially stifling necessary debates.' },
  { name: 'Catalyst',         description: 'Introduces new ideas and energizes discussions.' },
  { name: 'Socrates',         description: 'Asks thoughtful, often elusive and rhetorical questions.' },
  { name: 'Yoda',             description: 'Summarizes key insights effectively.' },
  { name: 'Multitasker',      description: 'Frequently distracted by devices.' },
  { name: 'Over-analyzer',    description: 'Gets buried in data and analysis, hindering decision-making.' },
];
